import React from 'react';
import ApolloClient, { gql } from 'apollo-boost';
import { ApolloProvider, useQuery } from '@apollo/react-hooks';
import { Button, Col, Row, Skeleton, Card } from 'antd';
import styled from 'styled-components';
import LazyLoad from 'react-lazyload';
import Meta from 'antd/lib/card/Meta';
import ReactPlayer from 'react-player/lazy';

import Layout from '../../components/layout';
import SEO from '../../components/seo';
import { SettingProvider } from '../../lib/context/settings';
import { AdsProvider } from '../../lib/context/ads';
import {
  SecondarySectionTitle,
  SecondaryTitle,
  Title,
} from '../../components/styles';
import { device } from '../../lib/theme';
import Center from '../../stories/helpers/center';
import wpImageUrl from '../../lib/wp-image-url';

const HeroContainer = styled(Row)`
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  background-image: url(https:\/\/aaacdndev.blob.core.windows.net/wordpress/travel/on-demand/travel-on-demand-cover%402x.jpg);
  height: 500px;
  margin: auto;

  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }

    background-position: center center;
  }
`;

const PlayerWrapper = styled.div`
  height: 45%;
  min-height: 100px;
  position: relative;

  > div {
    height: 100% !important;
    width: 100% !important;
    min-height: 100px;
    cursor: default !important;
    position: absolute;
  }

  .react-player__shadow {
    display: none !important;
  }
`;

const PromoSectionContainer = styled(Row)`
  padding: 8px 0;

  .ant-card-body {
    min-height: ${({ longForm }: { longForm: boolean }) =>
      longForm ? 300 : 160}px;
    position: relative;
  }
  .ant-card-meta-title {
    margin-bottom: 0 !important;
  }
  .ant-card-meta-description {
    font-family: 'CG-regular', sans-serif;
    font-size: 16px;
    line-height: 20px;
    color: #2c2c2c;
    min-height: 140px;
  }

  .ant-card .promo-action {
    background: transparent;
    border: none;
    color: #cd2127;
    font-family: 'CG-bold', sans-serif;
    font-weight: 400;
    font-size: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 24px;
  }

  .ant-card .promo-action > img {
    margin-left: 8px;
  }

  .ant-carousel {
    width: 100%;
  }

  @media ${device.tablet} {
    padding: 64px 0;
  }
`;

const emptyPromos = new Array(4).fill('');

const positionAbsolute: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const VIDEOS_QUERY = gql`
  query Videos($category: [String]) {
    demandTypes(where: { name: $category }) {
      edges {
        node {
          travelOnDemandPosts(first: 100, where: { status: PUBLISH }) {
            nodes {
              uri
              title
              demandTypes(first: 10) {
                edges {
                  node {
                    name
                    uri
                    parent {
                      node {
                        name
                      }
                    }
                  }
                }
              }
              _ondemand_meta_ondemand_img
              _ondemand_meta_ondemand_code
              _ondemand_meta_ondemand_summary
            }
          }
        }
      }
    }
  }
`;

const OnDemandCover: React.FC<{ youtubeCode?: string; img?: string }> = ({
  youtubeCode,
  img,
}) => {
  if (img && img.trim().length > 0) {
    return (
      <div
        style={{
          backgroundImage: `url(${wpImageUrl(img)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '45%',
          minHeight: 100,
        }}
      />
    );
  }
  if (youtubeCode && youtubeCode.trim().length > 0) {
    return (
      <PlayerWrapper>
        <ReactPlayer
          url={`https://www.youtube.com/watch?v=${youtubeCode}`}
          light
          controls={false}
          playing={false}
        />
      </PlayerWrapper>
    );
  }
  return (
    <div
      style={{
        paddingTop: '45%',
        minHeight: 100,
      }}
    />
  );
};

const OnDemandVideos: React.FC<{ category: string }> = ({ category }) => {
  const { loading, error, data } = useQuery(VIDEOS_QUERY, {
    variables: { category },
  });

  if (loading) {
    return (
      <Row>
        <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
          <PromoSectionContainer
            gutter={[16, 16]}
            style={{ margin: 0 }}
            longForm={false}
          >
            {emptyPromos.map((_, idx) => (
              <Col xs={{ span: 12 }} lg={{ span: 6 }} key={idx}>
                <Card
                  hoverable
                  cover={
                    <div
                      className="ant-skeleton ant-skeleton-active"
                      style={{
                        paddingTop: '45%',
                        position: 'relative',
                      }}
                    >
                      <div
                        className="ant-skeleton-content"
                        style={positionAbsolute}
                      >
                        <ul
                          className="ant-skeleton-paragraph"
                          style={{ ...positionAbsolute, marginBottom: 0 }}
                        >
                          <li
                            style={{ ...positionAbsolute, height: 'auto' }}
                          ></li>
                        </ul>
                      </div>
                    </div>
                  }
                >
                  <Skeleton active />
                  <Skeleton.Button active />
                </Card>
              </Col>
            ))}
          </PromoSectionContainer>
        </Col>
      </Row>
    );
  }

  if (error) {
    console.error(error);
    return (
      <Row>
        <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
          <Center>Error</Center>
          <Center>Please try disabling Ad Blockers.</Center>
          <Center style={{ fontSize: 10 }}>{error.message}</Center>
        </Col>
      </Row>
    );
  }

  const videosList = data
    ? data.demandTypes.edges[0].node.travelOnDemandPosts.nodes.map(
        (node: any) => ({
          id: node.uri,
          title: node.title,
          img: node._ondemand_meta_ondemand_img,
          url: node.uri,
          youtubeCode: node._ondemand_meta_ondemand_code,
          description: node._ondemand_meta_ondemand_summary,
        })
      )
    : [];

  return (
    <Row>
      <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
        <PromoSectionContainer
          gutter={[16, 16]}
          style={{ margin: 0 }}
          longForm={false}
        >
          {videosList.map((data) => (
            <Col xs={{ span: 12 }} lg={{ span: 6 }} key={data.id}>
              <Card hoverable cover={<OnDemandCover {...data} />}>
                <Meta
                  title={
                    data.title ? (
                      <SecondarySectionTitle
                        title={data.title}
                        marginBottom={16}
                      >
                        {data.title}
                      </SecondarySectionTitle>
                    ) : null
                  }
                  description={
                    <div
                      style={{ paddingBottom: 30 }}
                      dangerouslySetInnerHTML={{ __html: data.description }}
                    />
                  }
                />

                <a className="promo-action" href={data.url}>
                  Watch
                  <img src="/img/icon/right-arrow.png" />
                </a>
              </Card>
            </Col>
          ))}
        </PromoSectionContainer>
      </Col>
    </Row>
  );
};

const OnDemandVideosSection: React.FC<{
  active: boolean;
  category: string;
}> = ({ active, category }) => {
  return (
    <div style={{ display: active ? 'block' : 'none', width: '100%' }}>
      <LazyLoad once height={300} key={active}>
        <OnDemandVideos category={category} />
      </LazyLoad>
    </div>
  );
};

const OnDemand: React.FC<any> = () => {
  const [activeSection, setActiveSection] = React.useState('tips');

  const setActive = React.useCallback((key: string) => {
    return () => setActiveSection(key);
  }, []);

  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="TRAVEL ON-DEMAND"
              description="Podcasts & Videos"
              canonicalUrl="/travel/on-demand"
              image="https://aaacdndev.blob.core.windows.net/wordpress/travel/on-demand/travel-on-demand-cover%402x.jpg"
            />

            <HeroContainer align="middle" justify="center">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title white>TRAVEL ON-DEMAND</Title>
                <SecondaryTitle white>Podcasts & Videos</SecondaryTitle>
              </Col>
            </HeroContainer>

            <Row style={{ paddingTop: 48, marginBottom: 128 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Row>
                  <div style={{ display: 'flex' }}>
                    <Button onClick={setActive('tips')}>Travel Tips</Button>
                    <Button onClick={setActive('international')}>
                      International
                    </Button>
                    <Button onClick={setActive('domestic')}>Domestic</Button>
                    <Button onClick={setActive('cruises')}>Cruises</Button>
                    <Button onClick={setActive('family')}>
                      Family Friendly Trips
                    </Button>
                    <Button onClick={setActive('seminars')}>
                      AAA Travel Seminars
                    </Button>
                  </div>
                </Row>
                <Row>
                  <OnDemandVideosSection
                    category="Travel Tips"
                    active={activeSection === 'tips'}
                  />
                  <OnDemandVideosSection
                    category="International"
                    active={activeSection === 'international'}
                  />
                  <OnDemandVideosSection
                    category="Domestic"
                    active={activeSection === 'domestic'}
                  />
                  <OnDemandVideosSection
                    category="Cruises"
                    active={activeSection === 'cruises'}
                  />
                  <OnDemandVideosSection
                    category="Family Friendly Trips"
                    active={activeSection === 'family'}
                  />
                  <OnDemandVideosSection
                    category="Seminars"
                    active={activeSection === 'seminars'}
                  />
                </Row>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default OnDemand;
